import { z } from 'zod'

/**
 * `GET /api/help/search` (`PR-210`/`API-103`/`AC-262`). Server-side twin of
 * `apps/web/src/lib/help-search-index.ts`'s client-side search, for callers
 * without JS (crawlers) and to give a single non-duplicated ranking
 * implementation a stable contract other services can call.
 */
export const helpSearchQuerySchema = z.object({
  q: z.string().trim().min(1).max(200),
  locale: z.enum(['zh-TW', 'en']).default('zh-TW'),
  limit: z.coerce.number().int().min(1).max(20).default(10),
})

export type HelpSearchQuery = z.infer<typeof helpSearchQuerySchema>

export const helpSearchResultSchema = z.object({
  id: z.string(),
  url: z.string(),
  title: z.string(),
  description: z.string(),
  role: z.string(),
  category: z.string(),
  type: z.string(),
  score: z.number(),
})

export type HelpSearchResultDto = z.infer<typeof helpSearchResultSchema>

export const helpSearchResponseSchema = z.object({
  results: z.array(helpSearchResultSchema),
})

export type HelpSearchResponseDto = z.infer<typeof helpSearchResponseSchema>

/**
 * `POST /api/help/search-analytics` (`PR-213`/`API-104`/`SEC-046`/
 * `DATA-053`, `AC-265`, backlog item 5). Batched client-side (the client
 * queues events and flushes periodically / on page unload rather than
 * firing one request per keystroke — see
 * `apps/web/src/lib/help-search-analytics.ts`), so the request body is
 * always an array. `queryText` is capped at the same 200 chars as
 * `helpSearchQuerySchema.q` (the query that produced this event was itself
 * already validated against that same limit when it was searched).
 */
export const helpSearchAnalyticsEventSchema = z.object({
  queryText: z.string().trim().min(1).max(200),
  locale: z.enum(['zh-TW', 'en']),
  resultCount: z.number().int().min(0),
  /** The `/help`/`/guide`/`/howto` document id the visitor clicked from this search's results, if any. */
  clickedResultId: z.string().max(200).nullable(),
})

export type HelpSearchAnalyticsEvent = z.infer<typeof helpSearchAnalyticsEventSchema>

const MAX_ANALYTICS_EVENTS_PER_BATCH = 20

export const helpSearchAnalyticsBatchRequestSchema = z.object({
  events: z.array(helpSearchAnalyticsEventSchema).min(1).max(MAX_ANALYTICS_EVENTS_PER_BATCH),
})

export type HelpSearchAnalyticsBatchRequest = z.infer<typeof helpSearchAnalyticsBatchRequestSchema>

/**
 * `POST /api/help/articles/:articleId/feedback` (`PR-214`/`API-105`/
 * `SEC-047`/`DATA-054`, backlog item 6). `honeypot` is a hidden form field
 * real visitors never fill in (see
 * `apps/web/src/components/help/article-feedback.tsx`); this schema
 * deliberately ACCEPTS any short string there rather than rejecting a
 * non-empty value — a bot that gets a validation error back learns the
 * field exists and to leave it blank next time. The route handler
 * (`apps/api/src/routes/help-search.ts`) reads it and, if non-empty,
 * silently drops the submission (still responds success) instead.
 */
export const helpArticleFeedbackRequestSchema = z.object({
  helpful: z.boolean(),
  comment: z
    .string()
    .trim()
    .max(2000)
    .nullable()
    .optional()
    .transform((value) => (value === undefined || value === '' ? null : value)),
  locale: z.enum(['zh-TW', 'en']),
  // Accepts (does not reject) any value here, deliberately — see the doc
  // comment above. Capped at a generous length purely to bound storage/
  // payload size, not as a validation signal.
  honeypot: z.string().max(200).optional(),
})

export type HelpArticleFeedbackRequest = z.infer<typeof helpArticleFeedbackRequestSchema>

/**
 * `GET /api/platform/help-search-analytics` (`PR-220`/`API-107`, backlog
 * items 5/6 follow-up). Minimal read/report API for the two anonymous
 * `/help` tables (`AC-265` search analytics, `AC-266` article feedback) —
 * neither had a viewer when originally shipped, tracked as a known gap in
 * both delivery logs. Pagination shape matches the existing
 * `platformAuditLogQuerySchema` (`packages/schemas/src/platform.ts`)
 * exactly, for consistency with the rest of `/api/platform/*`.
 */
export const platformHelpSearchAnalyticsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  /** Filter to only events where a result was (or was not) clicked. */
  clicked: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => (value === undefined ? undefined : value === 'true')),
})

export type PlatformHelpSearchAnalyticsQuery = z.infer<
  typeof platformHelpSearchAnalyticsQuerySchema
>

export const platformHelpSearchAnalyticsEventDtoSchema = z.object({
  id: z.string(),
  queryText: z.string(),
  locale: z.string(),
  resultCount: z.number(),
  clickedResultId: z.string().nullable(),
  createdAt: z.string(),
})

export type PlatformHelpSearchAnalyticsEventDto = z.infer<
  typeof platformHelpSearchAnalyticsEventDtoSchema
>

/**
 * `GET /api/platform/help-article-feedback` (`PR-220`/`API-107`). Same
 * pagination shape as the search-analytics report above.
 */
export const platformHelpArticleFeedbackQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  articleId: z.string().max(200).optional(),
  helpful: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => (value === undefined ? undefined : value === 'true')),
})

export type PlatformHelpArticleFeedbackQuery = z.infer<
  typeof platformHelpArticleFeedbackQuerySchema
>

export const platformHelpArticleFeedbackDtoSchema = z.object({
  id: z.string(),
  articleId: z.string(),
  helpful: z.boolean(),
  comment: z.string().nullable(),
  locale: z.string(),
  createdAt: z.string(),
})

export type PlatformHelpArticleFeedbackDto = z.infer<typeof platformHelpArticleFeedbackDtoSchema>

/**
 * `POST /api/help/ui-events` (`PR-230`/`API-110`/`SEC-049`/`DATA-056`,
 * `AC-286`, P1 backlog "Analytics", extending `help_search`'s existing
 * fixed-shape sibling tables with the 5 more general UI event names spec
 * §35 lists: `help_view`/`help_role_select`/`help_task_click`/
 * `help_quick_start_click`/`help_guide_click`. One generic event shape
 * (`eventName` selects which of `role`/`taskId`/`position` are meaningful)
 * rather than 5 near-identical schemas — see `migrations/0034_help_ui_events.sql`'s
 * doc comment for the same reasoning applied to the table itself.
 * `help_task_click` requires `taskId` (spec §36's `task` property); every
 * other event name leaves `taskId`/`position` unset.
 */
export const helpUiEventNameSchema = z.enum([
  'help_view',
  'help_role_select',
  'help_task_click',
  'help_quick_start_click',
  'help_guide_click',
])

export type HelpUiEventName = z.infer<typeof helpUiEventNameSchema>

export const helpUiEventSchema = z
  .object({
    eventName: helpUiEventNameSchema,
    role: z.enum(['student', 'judge', 'organizer', 'admin']).nullable(),
    taskId: z.string().max(200).nullable().optional(),
    position: z.number().int().min(0).nullable().optional(),
    locale: z.enum(['zh-TW', 'en']),
  })
  .refine((event) => event.eventName !== 'help_task_click' || event.taskId != null, {
    message: 'taskId is required when eventName is "help_task_click"',
    path: ['taskId'],
  })

export type HelpUiEvent = z.infer<typeof helpUiEventSchema>

const MAX_UI_EVENTS_PER_BATCH = 20

export const helpUiEventsBatchRequestSchema = z.object({
  events: z.array(helpUiEventSchema).min(1).max(MAX_UI_EVENTS_PER_BATCH),
})

export type HelpUiEventsBatchRequest = z.infer<typeof helpUiEventsBatchRequestSchema>

/**
 * `GET /api/platform/help/ui-events` (`PR-230`/`API-110`). Same minimal
 * read/report pattern as the other two `/help` analytics tables
 * (`platformHelpSearchAnalyticsQuerySchema`/`platformHelpArticleFeedbackQuerySchema`
 * above) — shipped with a viewer from the start this time, rather than
 * repeating the "write-only, no report" gap those two originally shipped
 * with and had to close later (`PR-220`/`API-107`).
 */
export const platformHelpUiEventsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  eventName: helpUiEventNameSchema.optional(),
})

export type PlatformHelpUiEventsQuery = z.infer<typeof platformHelpUiEventsQuerySchema>

export const platformHelpUiEventDtoSchema = z.object({
  id: z.string(),
  eventName: z.string(),
  role: z.string().nullable(),
  taskId: z.string().nullable(),
  position: z.number().nullable(),
  locale: z.string(),
  createdAt: z.string(),
})

export type PlatformHelpUiEventDto = z.infer<typeof platformHelpUiEventDtoSchema>
